import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type FormSectionSpacing = 'sm' | 'md' | 'lg';

interface FormSectionProps {
  title?: ReactNode;
  description?: ReactNode;
  /** Actions header à droite (boutons, badges). */
  actions?: ReactNode;
  /** Espacement vertical entre enfants directs (généralement des FormGrid/FormField). */
  spacing?: FormSectionSpacing;
  /** ID HTML pour ancres et nav scroll-spy. */
  id?: string;
  className?: string;
  children: ReactNode;
}

const SPACING_CLS: Record<FormSectionSpacing, string> = {
  sm: 'space-y-3',
  md: 'space-y-4',
  lg: 'space-y-5',
};

/**
 * Section de formulaire — titre + description + actions + corps.
 *
 * Container léger (sans border/shadow par défaut) pour permettre la composition
 * dans une page de settings : on l'utilise directement OU encapsulé dans un
 * `<SectionCard>` quand on veut un cadre visuel.
 *
 * @example
 *   <FormSection
 *     id="identite"
 *     title="Identité"
 *     description="Informations visibles par votre équipe."
 *   >
 *     <FormGrid columns={2}>
 *       <FormField ...>...</FormField>
 *       <FormField ...>...</FormField>
 *     </FormGrid>
 *   </FormSection>
 */
export function FormSection({
  title,
  description,
  actions,
  spacing = 'md',
  id,
  className,
  children,
}: FormSectionProps) {
  const hasHeader = title || description || actions;

  return (
    <section id={id} className={cn('min-w-0', className)}>
      {hasHeader && (
        <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {title && (
              <h3 className="text-[15px] font-semibold leading-tight text-charcoal-950 dark:text-charcoal-50">
                {title}
              </h3>
            )}
            {description && (
              <p className="mt-1 text-xs text-slate-500 dark:text-charcoal-300">{description}</p>
            )}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={SPACING_CLS[spacing]}>{children}</div>
    </section>
  );
}
